<template>
    <div>
        <div
                :class="classList"
                tabindex="0"
                role="option"
                data-click-option
                :aria-selected="String(selected)"
                @click="handleClick"
                @keyup.enter="handleClick"
        >
            <div
                    data-image
                    :class="classListImageWrapper"
                    class="flex-shrink-0"
            >
                <Thumbnail
                        v-if="image || color"
                        data-thumbnail
                        :image="image"
                        :color="color"
                        :selected="selected"
                        :class="disabled ? 'opacity-50' : ''"
                />
                <Checkmark
                        :checked="selected"
                        class="absolute top-1/8 left-1/8 -translate-center z-20 m-1"
                />
            </div>
            <div
                    v-if="heading || price"
                    :class="classListDetails"
            >
                <div
                        data-heading
                        :class="classListHeading"
                >
                    <span v-html="capitalizeFirstLetter(heading)"></span>
                    <span v-if="detailed && price !== null" :class="classListPrice">{{ $filters.currency(price) }}</span>
                </div>
                <div
                        v-if="supplementaryHeading"
                        data-supplementary-heading
                        :class="classListsupplementaryHeading"
                        v-html="supplementaryHeading"
                />
                <div v-if="subheading" data-subheading class="text-xs flex overflow-hidden" v-html="subheading"></div>
                <div
                        v-if="(!detailed && price !== null && !supplementaryHeading) || discountedPrice"
                        :class="classListPrices"
                >
                    <div
                            v-if="!detailed && price !== null && !supplementaryHeading"
                    >
                        <span v-if="discountedPrice" class="sr-only">{{ $t('message.builder.totalPriceLabel') }}</span>
                        <span data-price data-test-id="before-discount-price" class="text-xs" :class="classListPrice">{{ $filters.currency(price) }}</span>
                    </div>
                    <div v-if="discountedPrice">
                        <span class="sr-only">{{ $t('message.builder.discountedPriceLabel') }}</span>
                        <span data-discounted-price data-test-id="discounted-price" class="text-xs ml-2 font-bold text-blue-500" :class="{ 'text-gray-300': disabled }">
            {{ $filters.currency(discountedPrice) }}
          </span>
                    </div>
                </div>
                <div v-if="placeholder" data-price :class="classListPrice" />
            </div>
        </div>
        <slot></slot>
    </div>
</template>

<script>
import Checkmark from '@vuedir/components/Checkmark'
import Thumbnail from '@vuedir/components/Thumbnail'

/**
 * Option.vue
 */
export default {
  components: {
    Checkmark,
    Thumbnail,
  },

  props: {
    heading: {
      type: String,
      default: null,
    },

    subheading: {
      type: String,
    },

    supplementaryHeading: {
      type: String,
    },

    price: {
      type: Number,
      default: null,
    },

    discountedPrice: {
      type: Number,
      default: null,
    },

    selected: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    // eslint-disable-next-line vue/require-prop-types
    image: {
      default: null,
    },

    color: {
      type: String,
      default: '#ffffff'
    },

    compact: {
      type: Boolean,
      default: false,
    },

    detailed: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['click'],

  computed: {
    classList () {
      return [
        'flex cursor-pointer',
        this.selected && !this.compact ? 'bg-gray-50' : '',
        this.compact ? 'md:mb-4 md:w-1/3 xl:w-1/4 flex-col' : 'md:flex-row md:items-center md:w-full',
      ]
    },

    classListImageWrapper () {
      return [
        'relative bg-gray-50 flex-shrink-0',
        this.compact ? 'md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-20 xl:h-20' : '',
        this.placeholder ? 'bg-gray-100' : '',
        this.detailed ? 'w-24 h-24' : 'w-16 h-16'
      ]
    },

    classListDetails () {
      return [
        'pt-4 px-4 w-full md:pt-0',
        this.compact ? 'md:px-0 md:pt-1' : 'md:mt-0 md:px-4',
      ]
    },

    classListHeading () {
      return [
        'leading-none',
        this.compact ? 'mt-1' : '',
        this.disabled ? 'text-gray-300' : '',
        this.placeholder ? 'bg-gray-100 w-16 h-4' : '',
        this.detailed ? 'flex items-center justify-between' : '',
      ]
    },

    classListsupplementaryHeading () {
      return [
        'text-xs mt-1',
        this.disabled ? 'text-gray-300' : '',
        this.placeholder ? 'bg-gray-100 w-16 h-4' : '',
      ]
    },

    classListPrices () {
      return [
        'flex',
        this.compact ? 'mt-1' : '',
      ]
    },

    classListPrice () {
      return [
        this.discountedPrice ? 'line-through' : '',
        this.disabled ? 'text-gray-300' : '',
        this.discountedPrice && !this.disabled ? 'text-gray-400' : '',
        !this.discountedPrice && !this.disabled ? 'font-bold text-blue-500' : '',
        this.placeholder ? 'w-12 h-3 bg-gray-100' : '',
      ]
    },
  },

  methods: {
    handleClick () {
      if (!this.disabled) {
        this.$emit('click')
      }
    },
    capitalizeFirstLetter (text) {
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    }
  },
}
</script>
